@if (isOpen()) {
  <section class="checklist-creator">
    <div class="checklist-creator__header">
      <div>
        <div class="checklist-creator__eyebrow">{{ i18n.t('checklistCreator.eyebrow') }}</div>
        <h2 class="checklist-creator__title">{{ i18n.t('checklistCreator.title') }}</h2>
        @if (isChecklistActive()) {
          <div class="checklist-creator__active-note">
            <span>
              <span class="app-message__icon app-message__icon--info fa-solid fa-circle-info" aria-hidden="true"></span>
              {{ i18n.t('checklistCreator.activeReadOnlyNote') }}
            </span>
            <button
              class="secondary-button secondary-button--compact secondary-button--indigo"
              type="button"
              [disabled]="isCloningChecklist()"
              (click)="cloneChecklist()"
            >
              <span class="fa-solid fa-code-branch" aria-hidden="true"></span>
              {{
                isCloningChecklist()
                  ? i18n.t('checklistCreator.actions.cloningChecklist')
                  : i18n.t('checklistCreator.actions.cloneChecklist')
              }}
            </button>
          </div>
        }
      </div>
      @if (isReadOnly()) {
        <span class="app-status-chip checklist-creator__readonly-badge">
          {{ readOnlyBadgeLabel() }}
        </span>
      }
    </div>

    <ol class="app-stepper">
      <li
        class="app-stepper__item"
        [class.app-stepper__item--active]="currentStep() === 1"
        [class.app-stepper__item--complete]="checklistCreated()"
        [class.app-stepper__item--clickable]="availableStep() >= 1"
        (click)="goToStep(1)"
      >
        <span class="app-stepper__index">1</span>
        <div class="app-stepper__copy">
          <div class="app-stepper__title">{{ i18n.t('checklistCreator.steps.checklist.title') }}</div>
          <div class="app-stepper__meta">{{ i18n.t('checklistCreator.steps.checklist.meta') }}</div>
        </div>
        @if (checklistCreated()) {
          <span class="app-stepper__complete-icon fa-solid fa-check" aria-hidden="true"></span>
        }
      </li>
      <li
        class="app-stepper__item"
        [class.app-stepper__item--active]="currentStep() === 2"
        [class.app-stepper__item--complete]="canContinueToItems()"
        [class.app-stepper__item--clickable]="availableStep() >= 2"
        (click)="goToStep(2)"
      >
        <span class="app-stepper__index">2</span>
        <div class="app-stepper__copy">
          <div class="app-stepper__title">{{ i18n.t('checklistCreator.steps.groups.title') }}</div>
          <div class="app-stepper__meta">{{ i18n.t('checklistCreator.steps.groups.meta') }}</div>
        </div>
        @if (canContinueToItems()) {
          <span class="app-stepper__complete-icon fa-solid fa-check" aria-hidden="true"></span>
        }
      </li>
      <li
        class="app-stepper__item"
        [class.app-stepper__item--active]="currentStep() === 3"
        [class.app-stepper__item--complete]="hasQuestionsInEveryGroup()"
        [class.app-stepper__item--clickable]="availableStep() >= 3"
        (click)="goToStep(3)"
      >
        <span class="app-stepper__index">3</span>
        <div class="app-stepper__copy">
          <div class="app-stepper__title">{{ i18n.t('checklistCreator.steps.items.title') }}</div>
          <div class="app-stepper__meta">{{ i18n.t('checklistCreator.steps.items.meta') }}</div>
        </div>
        @if (hasQuestionsInEveryGroup()) {
          <span class="app-stepper__complete-icon fa-solid fa-check" aria-hidden="true"></span>
        }
      </li>
    </ol>

    @if (currentStep() === 1) {
      <section class="app-panel">
        <div class="app-panel__header">
          <h3 class="app-panel__title">{{ i18n.t('checklistCreator.checklistSection.title') }}</h3>
          <p class="app-panel__meta">{{ i18n.t('checklistCreator.checklistSection.meta') }}</p>
        </div>

        <form class="checklist-creator__form-grid" [formGroup]="checklistForm">
          <label class="app-field">
            <span class="app-field__label">{{ i18n.t('checklistCreator.fields.title') }}</span>
            <input type="text" formControlName="title" [placeholder]="i18n.t('checklistCreator.placeholders.title')" />
          </label>

          <label class="app-field">
            <span class="app-field__label">{{ i18n.t('checklistCreator.fields.version') }}</span>
            <input type="number" formControlName="version" min="1" />
          </label>

          <label class="app-field checklist-creator__field-full">
            <span class="app-field__label">{{ i18n.t('checklistCreator.fields.description') }}</span>
            <textarea class="app-textarea" formControlName="description" [placeholder]="i18n.t('checklistCreator.placeholders.description')"></textarea>
          </label>
        </form>

        @if (stepError(); as error) {
          <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
        }

        <div class="app-panel__actions">
          <button class="secondary-button secondary-button--indigo checklist-creator__close-action" type="button" (click)="close()">
            {{ i18n.t('checklistCreator.actions.close') }}
          </button>
          @if (!isReadOnly()) {
            @if (!isChecklistActive()) {
              <button
                class="secondary-button secondary-button--indigo"
                type="button"
                [disabled]="isPublishingChecklist()"
                [attr.title]="i18n.t('checklistCreator.actions.publishChecklist')"
                (click)="publishChecklist()"
              >
                {{ isPublishingChecklist() ? i18n.t('checklistCreator.actions.publishingChecklist') : i18n.t('checklistCreator.actions.publishChecklist') }}
              </button>
            }
            <button class="primary-button" type="button" [disabled]="isSubmittingStep()" (click)="saveChecklist()">
              @if (isSubmittingStep()) {
                {{ isEditingChecklist() ? i18n.t('checklistCreator.actions.updatingChecklist') : i18n.t('checklistCreator.actions.creatingChecklist') }}
              } @else {
                {{ i18n.t('checklistCreator.actions.saveAndContinue') }}
              }
            </button>
          }
        </div>
      </section>
    }

    @if (currentStep() === 2) {
      <section class="app-panel">
        <div class="app-panel__header">
          <h3 class="app-panel__title">{{ i18n.t('checklistCreator.groupsSection.title') }}</h3>
          <p class="app-panel__meta">{{ i18n.t('checklistCreator.groupsSection.meta') }}</p>
        </div>

        <form class="checklist-creator__group-form" [formGroup]="groupForm">
          <label class="app-field checklist-creator__group-input">
            <span class="app-field__label">{{ i18n.t('checklistCreator.fields.groupName') }}</span>
            <input type="text" formControlName="name" [placeholder]="i18n.t('checklistCreator.placeholders.groupName')" />
          </label>

          @if (!isReadOnly()) {
            @if (isEditingGroup()) {
              <button class="secondary-button secondary-button--compact secondary-button--indigo" type="button" [disabled]="isSavingGroup()" (click)="cancelGroupEdit()">
                {{ i18n.t('common.actions.cancel') }}
              </button>
            }
            <button class="secondary-button secondary-button--compact secondary-button--indigo" type="button" [disabled]="isSavingGroup()" (click)="addGroup()">
              <span class="fa-solid fa-layer-group" aria-hidden="true"></span>
              {{
                isEditingGroup()
                  ? i18n.t('checklistCreator.actions.saveGroup')
                  : i18n.t('checklistCreator.actions.addGroup')
              }}
            </button>
          }
        </form>

        @if (groupError(); as error) {
          <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
        }

        <div class="checklist-creator__collection">
          @for (group of createdGroups(); track group.id) {
            <div class="app-list-row checklist-creator__group-row">
              <div class="checklist-creator__list-row-copy">
                <div class="app-list-row__title">{{ group.name }}</div>
              </div>
              <div class="checklist-creator__list-row-count">
                <span class="checklist-creator__list-row-count-label">{{ i18n.t('checklistCreator.fields.questionsCount') }}</span>
                <span class="checklist-creator__list-row-count-value">{{ getGroupItemsCount(group.id) }}</span>
              </div>
              @if (!isReadOnly()) {
                <div class="app-row-actions">
                  <button
                    class="secondary-button secondary-button--compact secondary-button--indigo app-row-action-button"
                    type="button"
                    [disabled]="isSavingGroup() || isDeletingEntity()"
                    [attr.title]="i18n.t('checklistCreator.actions.editGroup')"
                    [attr.aria-label]="i18n.t('checklistCreator.actions.editGroup')"
                    (click)="editGroup(group)"
                  >
                    <span class="fa-solid fa-pen-to-square" aria-hidden="true"></span>
                  </button>
                  <button
                    class="app-button--danger primary-button primary-button--compact app-row-action-button"
                    type="button"
                    [disabled]="isDeletingEntity()"
                    [attr.title]="i18n.t('checklistCreator.actions.deleteGroup')"
                    [attr.aria-label]="i18n.t('checklistCreator.actions.deleteGroup')"
                    (click)="requestGroupDeletion(group)"
                  >
                    <span class="fa-solid fa-trash-can" aria-hidden="true"></span>
                  </button>
                </div>
              }
            </div>
          } @empty {
            <div class="app-empty-state"><span class="app-message__icon app-message__icon--info fa-solid fa-circle-info" aria-hidden="true"></span> {{ i18n.t('checklistCreator.empty.groups') }}</div>
          }
        </div>

        <div class="app-panel__actions">
          <button class="secondary-button secondary-button--indigo checklist-creator__close-action" type="button" (click)="close()">
            {{ i18n.t('checklistCreator.actions.close') }}
          </button>
          @if (!isReadOnly()) {
            @if (!isChecklistActive()) {
              <button
                class="secondary-button secondary-button--indigo"
                type="button"
                [disabled]="isPublishingChecklist()"
                [attr.title]="i18n.t('checklistCreator.actions.publishChecklist')"
                (click)="publishChecklist()"
              >
                {{ isPublishingChecklist() ? i18n.t('checklistCreator.actions.publishingChecklist') : i18n.t('checklistCreator.actions.publishChecklist') }}
              </button>
            }
            <button class="primary-button" type="button" [disabled]="!canContinueToItems()" (click)="goToItemsStep()">
              {{ i18n.t('checklistCreator.actions.continueToItems') }}
            </button>
          }
        </div>
      </section>
    }

    @if (currentStep() === 3) {
      <section class="app-panel">
        <div class="app-panel__header">
          <h3 class="app-panel__title">{{ i18n.t('checklistCreator.itemsSection.title') }}</h3>
          <p class="app-panel__meta">{{ i18n.t('checklistCreator.itemsSection.meta') }}</p>
        </div>

        <ng-template #questionForm>
          <form class="checklist-creator__form-grid" [formGroup]="itemForm">
            <label class="app-field">
              <span class="app-field__label">{{ i18n.t('checklistCreator.fields.questionTitle') }}</span>
              <input type="text" formControlName="title" [placeholder]="i18n.t('checklistCreator.placeholders.questionTitle')" />
            </label>

            <label class="app-field app-field--select">
              <span class="app-field__label">{{ i18n.t('checklistCreator.fields.itemType') }}</span>
              <select class="app-select" formControlName="itemType">
                @for (option of itemTypeOptions; track option.value) {
                  <option [value]="option.value">{{ i18n.t(option.labelKey) }}</option>
                }
              </select>
            </label>

            <label class="app-field">
              <span class="app-field__label">{{ i18n.t('checklistCreator.fields.maxScore') }}</span>
              <input type="number" min="0" formControlName="maxScore" />
            </label>

            <label class="app-switch">
              <input type="checkbox" formControlName="allowPhoto" />
              <span class="app-switch__indicator"></span>
              <span>{{ i18n.t('checklistCreator.fields.allowPhoto') }}</span>
            </label>

            <label class="app-field app-field--select checklist-creator__field-full">
              <span class="app-field__label">{{ i18n.t('checklistCreator.fields.targetGroup') }}</span>
              <select class="app-select" formControlName="groupId">
                <option value="" disabled>{{ i18n.t('checklistCreator.placeholders.targetGroup') }}</option>
                @for (group of createdGroups(); track group.id) {
                  <option [value]="group.id">{{ group.name }}</option>
                }
              </select>
            </label>
          </form>

          @if (selectedItemType() === 'yes_no') {
            <app-checklist-item-definition-yes-no [form]="itemFormGroup" [i18n]="i18n" />
          }

          @if (selectedItemType() === 'scale') {
            <app-checklist-item-definition-scale
              [form]="itemFormGroup"
              [buckets]="scaleBuckets"
              [i18n]="i18n"
              [isReadOnly]="isReadOnly()"
              [addBucketRequested]="addScaleBucketHandler"
              [removeBucketRequested]="removeScaleBucketHandler"
            />
          }

          @if (selectedItemType() === 'number') {
            <app-checklist-item-definition-number [form]="itemFormGroup" [i18n]="i18n" />
          }

          @if (selectedItemType() === 'number_with_unit') {
            <app-checklist-item-definition-number-with-unit
              [form]="itemFormGroup"
              [buckets]="numberWithUnitBuckets"
              [i18n]="i18n"
              [isReadOnly]="isReadOnly()"
              [addBucketRequested]="addNumberWithUnitBucketHandler"
              [removeBucketRequested]="removeNumberWithUnitBucketHandler"
            />
          }

          @if (selectedItemType() === 'multiscale') {
            <app-checklist-item-definition-multiscale
              [options]="multiscaleOptions"
              [i18n]="i18n"
              [isReadOnly]="isReadOnly()"
              [addOptionRequested]="addMultiscaleOptionHandler"
              [removeOptionRequested]="removeMultiscaleOptionHandler"
            />
          }

          @if (itemError(); as error) {
            <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
          }

          @if (!isReadOnly()) {
            <div class="app-panel__actions checklist-creator__question-actions">
              @if (isEditingItem()) {
                <button class="secondary-button secondary-button--indigo" type="button" [disabled]="isSavingItem()" (click)="cancelItemEdit()">
                  {{ i18n.t('common.actions.cancel') }}
                </button>
              }
              <button class="primary-button" type="button" [disabled]="isSavingItem()" (click)="addItem()">
                @if (isSavingItem()) {
                  {{ i18n.t('checklistCreator.actions.savingQuestion') }}
                } @else {
                  {{
                    isEditingItem()
                      ? i18n.t('checklistCreator.actions.saveQuestion')
                      : i18n.t('checklistCreator.actions.addQuestion')
                  }}
                }
              </button>
            </div>
          }
        </ng-template>

        @if (isAddingItem() || isReadOnly()) {
          <div class="checklist-creator__question-form" id="new-question-form">
            <ng-container [ngTemplateOutlet]="questionForm" />
          </div>
        } @else {
          <div class="app-panel__actions">
            <button
              class="secondary-button secondary-button--indigo"
              type="button"
              [disabled]="isSavingItem() || isDeletingEntity()"
              aria-expanded="false"
              aria-controls="new-question-form"
              (click)="openNewItem()"
            >
              <span class="fa-solid fa-plus" aria-hidden="true"></span>
              {{ i18n.t('checklistCreator.actions.addNewQuestion') }}
            </button>
          </div>
          @if (!isEditingItem() && itemError(); as error) {
            <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
          }
        }

        @if (publishError(); as error) {
          <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
        }

        <div class="checklist-creator__collection checklist-creator__collection--stacked">
          @for (entry of groupedItems(); track entry.group.id) {
            <section class="app-panel app-panel--nested">
              <div class="app-panel__header">
                <h4 class="app-panel__title">{{ entry.group.name }}</h4>
              </div>
              @for (item of entry.items; track item.id) {
                <div class="app-list-row checklist-creator__item-row">
                  <div class="checklist-creator__list-row-copy">
                    <div class="app-list-row__title">{{ item.title }}</div>
                  </div>
                  <span
                    class="checklist-creator__item-type-badge"
                    [ngClass]="getItemTypeBadgeClass(item.item_type)"
                  >
                    {{ getItemTypeShortLabel(item.item_type) }}
                  </span>
                  @if (!isReadOnly()) {
                    <div class="app-row-actions">
                      <button
                        class="secondary-button secondary-button--compact secondary-button--indigo app-row-action-button"
                        type="button"
                        [disabled]="isSavingItem() || isDeletingEntity()"
                        [attr.title]="i18n.t('checklistCreator.actions.editItem')"
                        [attr.aria-label]="i18n.t('checklistCreator.actions.editItem')"
                        [attr.aria-expanded]="editingItemId() === item.id"
                        [attr.aria-controls]="'question-editor-' + item.id"
                        (click)="editItem(entry.group.id, item)"
                      >
                        <span class="fa-solid fa-pen-to-square" aria-hidden="true"></span>
                      </button>
                      <button
                        class="app-button--danger primary-button primary-button--compact app-row-action-button"
                        type="button"
                        [disabled]="isDeletingEntity()"
                        [attr.title]="i18n.t('checklistCreator.actions.deleteItem')"
                        [attr.aria-label]="i18n.t('checklistCreator.actions.deleteItem')"
                        (click)="requestItemDeletion(entry.group.id, item)"
                      >
                        <span class="fa-solid fa-trash-can" aria-hidden="true"></span>
                      </button>
                    </div>
                  }
                  @if (editingItemId() === item.id && !isReadOnly()) {
                    <div class="checklist-creator__question-form checklist-creator__item-editor" [id]="'question-editor-' + item.id">
                      <ng-container [ngTemplateOutlet]="questionForm" />
                    </div>
                  }
                </div>
              } @empty {
                <div class="app-empty-state"><span class="app-message__icon app-message__icon--info fa-solid fa-circle-info" aria-hidden="true"></span> {{ i18n.t('checklistCreator.empty.items') }}</div>
              }
            </section>
          }
        </div>

        <div class="app-panel__actions checklist-creator__footer-actions">
          <button class="secondary-button secondary-button--indigo checklist-creator__close-action" type="button" (click)="close()">
            {{ i18n.t('checklistCreator.actions.close') }}
          </button>
          @if (!isReadOnly()) {
            @if (!isChecklistActive()) {
              <button
                class="secondary-button secondary-button--indigo"
                type="button"
                [disabled]="isPublishingChecklist()"
                [attr.title]="i18n.t('checklistCreator.actions.publishChecklist')"
                (click)="publishChecklist()"
              >
                {{ isPublishingChecklist() ? i18n.t('checklistCreator.actions.publishingChecklist') : i18n.t('checklistCreator.actions.publishChecklist') }}
              </button>
            }
          }
        </div>
      </section>
    }

    <app-confirmation-dialog
      [isOpen]="pendingDeletion() !== null"
      [title]="deleteConfirmationTitle()"
      [message]="deleteConfirmationMessage()"
      [confirmLabel]="isDeletingEntity() ? i18n.t('checklistCreator.actions.deleting') : i18n.t('common.actions.yes')"
      [cancelLabel]="i18n.t('common.actions.cancel')"
      [confirmDisabled]="isDeletingEntity()"
      (confirmed)="confirmDeletion()"
      (cancelled)="cancelDeletion()"
    />

    <app-confirmation-dialog
      [isOpen]="publishRequirementsDialogOpen()"
      [title]="i18n.t('checklistCreator.publishRequirements.title')"
      [message]="i18n.t('checklistCreator.errors.publishRequirements')"
      [confirmLabel]="i18n.t('common.actions.ok')"
      [cancelLabel]="i18n.t('common.actions.cancel')"
      [showCancel]="false"
      (confirmed)="closePublishRequirementsDialog()"
      (cancelled)="closePublishRequirementsDialog()"
    />

    <app-confirmation-dialog
      [isOpen]="publishSuccessDialogOpen()"
      [title]="i18n.t('checklistCreator.publishSuccess.title')"
      [message]="i18n.t('checklistCreator.publishSuccess.message')"
      [confirmLabel]="i18n.t('common.actions.ok')"
      [cancelLabel]="i18n.t('common.actions.cancel')"
      [showCancel]="false"
      (confirmed)="closePublishSuccessDialog()"
      (cancelled)="closePublishSuccessDialog()"
    />
  </section>
}
